import { dismissToast, type ToastifyId } from '@lokalise/louis';
import debounce from 'lodash/debounce';
import type { SagaIterator } from 'redux-saga';
import { eventChannel } from 'redux-saga';
import { getContext, select, takeEvery } from 'redux-saga/effects';

import { getSessionUserId } from '@app/selectors';
import type { bgProcessFinished, bgProcessUpdated } from '@app/sockets/socketActions';
import * as socketActions from '@app/sockets/socketActions';
import type { BgProcessFinishedPayload } from '@app/sockets/socketTypes';

import getEditorLink from '../../../utils/location';
import type { ActivityType, BackgroundActivityStartEvent } from '../events';
import { getHandler, type Handler, type HandlerType } from './background-actions';

type ProcessId = string;
const toastIds: Record<ProcessId, ToastifyId> = {};
const activityTypeToHandlerTypeMapping: Record<ActivityType, HandlerType> = {
    'tag-add': 'bulk-action-add-tag',
    'tag-remove': 'bulk-action-remove-tag',
    delete: 'bulk-action-delete-key',
    'apply-tm': 'bulk-apply-tm',
    'copy-keys': 'bulk-action-copy-keys',
    'move-keys': 'bulk-action-move-keys',
    proofread: 'bulk-action-review',
    unproofread: 'bulk-action-unreview',
    fillwithtext: 'bulk-action-fill-text',
    'bulk-apply-th': 'bulk-action-restore-translation',
};

const debounced: Record<ProcessId, ReturnType<typeof debounce>> = {};

type OnProgressArg = Parameters<Handler['onProgress']>[0];
type ScheduledUpdateArg = OnProgressArg & { type: string; id: ProcessId };

let reloading = false;

const scheduledUpdate = (payload: ScheduledUpdateArg) => {
    const handler = getHandler(payload.type as HandlerType);
    if (!handler) {
        return;
    }

    if (!toastIds[payload.id]) {
        toastIds[payload.id] = `bg-process:${Math.random().toFixed(10)}`;
    }

    const toastId = toastIds[payload.id];
    handler.onProgress(payload, toastId, true);
};

const scheduleUpdate = (payload: ScheduledUpdateArg) => {
    if (!debounced[payload.id]) {
        debounced[payload.id] = debounce(scheduledUpdate, 1000, { maxWait: 1000 });
    }

    debounced[payload.id](payload);

    return debounced[payload.id];
};

const cancelScheduledUpdate = (processId: ProcessId) => {
    if (debounced[processId]) {
        debounced[processId].cancel();
    }
};

function* showProcessProgressToast({ payload }: ReturnType<typeof bgProcessUpdated>) {
    const userId: string = yield select(getSessionUserId);
    if (typeof payload.user === 'undefined' || Number(userId) !== payload.user.id) {
        // Ignore processes from other users
        return;
    }

    if (payload.status !== 'running') {
        return;
    }

    const handler = getHandler(payload.type);
    if (!handler) {
        return;
    }

    const toastId = toastIds[payload.id];
    if (!toastId) {
        scheduleUpdate(payload);
    } else {
        handler.onProgress(payload, toastId);
    }
}

function* showProcessFinishedToast({ payload }: ReturnType<typeof bgProcessFinished>) {
    const userId: string = yield select(getSessionUserId);
    if (typeof payload === 'undefined' || Number(userId) !== payload.user.id) {
        // Ignore processes from other users
        return;
    }

    cancelScheduledUpdate(payload.id);

    const handler = getHandler(payload.type);
    if (!handler) {
        return;
    }

    /**
     * We need the "Finished" toast to be delayed by one cycle so we have opportunity to cancel it in another listener.
     */
    setTimeout(() => {
        if (reloading) {
            // Do nothing if we're reloading anyway.
            return;
        }

        const projectPage = getEditorLink(payload.params.project_id);

        const isOnProjectPage = window.location.toString().startsWith(projectPage);

        const toastId = toastIds[payload.id];
        if (toastId) {
            dismissToast(toastId);
        }

        handler.onFinished(payload, isOnProjectPage);
    }, 0);
}

type BackgroundActivityEventAction = { action: 'start'; payload: BackgroundActivityStartEvent };

const backgroundActivityEvents = eventChannel<BackgroundActivityEventAction>((emit) => {
    const eventHandler = (e: BackgroundActivityStartEvent) => {
        emit({ action: 'start', payload: e });
    };

    document.addEventListener('bg_activity_start', eventHandler);

    return () => {
        // istanbul ignore next -- We can't test this really
        document.removeEventListener('bg_activity_start', eventHandler);
    };
});

function* handleBackgroundActivityEvent({ payload }: BackgroundActivityEventAction): SagaIterator {
    const socketConnection = yield getContext('socketConnection');
    // istanbul ignore if -- We don't really care for this
    if (!socketConnection) {
        return;
    }

    const startTime = Date.now();
    const { type, keyCount } = payload.detail;
    const handlerType = activityTypeToHandlerTypeMapping[type];
    const tempProcessId = `${type}-${Math.random().toFixed(10)}`;
    const initialMockUpdatePayload = { bulk: { keyCount }, progress: '0', type: handlerType, id: tempProcessId };

    // This sets up initial progress toast with 0 progress (in case process doesn't start for a while so we show
    // some progress)
    const initialToast = scheduleUpdate(initialMockUpdatePayload);

    const finishProcess = (finishPayload: BgProcessFinishedPayload) => {
        const { id: processId } = finishPayload;
        // Cancel any pending update toasts
        initialToast.cancel();

        const toastId = toastIds[processId];

        const AUTO_RELOAD_PERIOD = 3000; // 3s
        const timeSinceStart = Date.now() - startTime;

        if (timeSinceStart < AUTO_RELOAD_PERIOD) {
            const handler = getHandler(handlerType);

            if (toastId) {
                // If we're already displaying the progress toast, we update progress to 100%
                handler?.onProgress({ ...initialMockUpdatePayload, progress: '100' }, toastId);
            }

            // Allow setting up notifications
            handler?.onEarlyFinish(finishPayload);

            reloading = true;
            window.location.reload();
        } else {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define -- no avoiding it here
            socketConnection.off('bg process finished', processFinishedListener);

            if (!toastId) {
                return;
            }

            // When task finished after more than reload period, we close the toast
            // and another toast for finishing will be triggered separately.
            dismissToast(toastId);
        }
    };

    const processFinishedListener = (finishPayload: BgProcessFinishedPayload) => {
        finishProcess(finishPayload);
    };

    socketConnection.on('bg process finished', processFinishedListener);

    // eslint-disable-next-line no-param-reassign
    payload.responseHandler = (processId) => {
        // In order to track toast before we get the process id, we created a temp process id. Once we get the real
        // process id, we update reference to process toast id.
        if (toastIds[tempProcessId]) {
            toastIds[processId] = toastIds[tempProcessId];
        } else {
            // This means the toast wasn't rendered yet
            // and we can update id of the initial mock update with the real process id
            initialMockUpdatePayload.id = processId;
        }
    };
}

export default function* initActivitySagas() {
    yield takeEvery(backgroundActivityEvents, handleBackgroundActivityEvent);
    yield takeEvery([socketActions.bgProcessUpdated.match], showProcessProgressToast);
    yield takeEvery([socketActions.bgProcessFinished.match], showProcessFinishedToast);
}
